import { initXSelects } from '../admin/x-select.js';
import {
  createLivePlayerController,
  setupPlayerDock,
  stopOtherLivePlayers,
  loadHlsScript,
  PAUSE_IDLE_STOP_MS,
  resolveDirectStreamUrl,
  resolveDirectListUrl,
  resolveDirectVideosUrl,
} from '../shared/live-stream-player.js';
import { trackStreamPlay } from '../shared/stream-play-tracker.js';
import { initStreamLiveRecorder } from '../shared/stream-live-recorder.js';
import { initStreamVodSaver } from '../shared/stream-vod-recorder.js';
import { initStreamVodGallery } from '../shared/stream-vod-gallery.js';

let liveLoadGeneration = 0;
let archiveLoadGeneration = 0;
let activeLiveController = null;
let activeVodCleanup = null;
let activeVodSaverCleanup = null;
let activeVodGalleryCleanup = null;
let activeRecorderCleanup = null;

function liveThumbFallbackHtml() {
  const tpl = document.getElementById('gv-live-thumb-fallback-tpl');
  if (tpl?.content?.firstElementChild) {
    return tpl.content.firstElementChild.outerHTML;
  }

  return (
    '<div class="gv-thumb-fallback gv-thumb-fallback--live" aria-hidden="true">' +
    '<svg class="gv-live-placeholder-svg" viewBox="0 0 80 48" aria-label="LIVE">' +
    '<rect width="80" height="48" rx="6" fill="currentColor" opacity="0.08"/>' +
    '<circle cx="40" cy="24" r="6" fill="#ff3b3b"/>' +
  '<text x="40" y="40" text-anchor="middle" font-size="9" font-weight="700" fill="currentColor" opacity="0.85">LIVE</text>' +
    '</svg></div>'
  );
}

function liveThumbStackHtml(previewUrl, fallbackUrl = '') {
  const src = (previewUrl || fallbackUrl || '').trim();

  if (!src) {
    return liveThumbFallbackHtml();
  }

  return (
    '<div class="gv-thumb-stack">' +
    liveThumbFallbackHtml() +
    `<img class="gv-thumb-img" src="${escapeHtml(src)}" alt="" loading="lazy" referrerpolicy="no-referrer">` +
    '</div>'
  );
}

function bindLiveThumbFallbacks(root) {
  (root || document).querySelectorAll('.gv-thumb-stack .gv-thumb-img').forEach((img) => {
    if (img.dataset.gvThumbBound === '1') {
      return;
    }

    img.dataset.gvThumbBound = '1';

    const showFallback = () => {
      img.style.display = 'none';
    };

    img.addEventListener('error', showFallback, { once: true });

    if (img.complete && img.naturalWidth === 0) {
      showFallback();
    }
  });
}

/** اگر تصویر بازی در هدر لود نشد، به‌جای آیکن شکسته کل قاب حذف می‌شود. */
function bindHeroPosterFallback(root) {
  const poster = (root || document).querySelector('.gv-hero__poster');
  const img = poster?.querySelector('img');
  if (!img) {
    return;
  }

  const drop = () => poster.remove();

  if (img.complete) {
    if (img.naturalWidth === 0) {
      drop();
    }
    return;
  }

  img.addEventListener('error', drop, { once: true });
}

function streamsEnabled(root) {
  const host = root || document.querySelector('.gv-page');
  return host?.dataset.streamsEnabled !== '0';
}

function deferListToClient(root) {
  const host = root || document.querySelector('.gv-page');
  return host?.dataset.gvDeferList === '1';
}

function reloadStreamLists(mode) {
  if (mode === 'live') {
    const mount = document.getElementById('gv-live-streams-mount');
    if (mount) {
      mount.innerHTML = liveSkeletonMarkup();
    }
    document.getElementById('gv-live-count')?.replaceChildren(document.createTextNode('…'));
    initLiveStreamsLoader();
    return;
  }

  archiveLoadGeneration += 1;
  const mount = document.getElementById('gv-archive-videos-mount');
  if (mount) {
    mount.innerHTML = loadingMarkup('در حال بارگذاری ویدیوها…');
  }
  document.getElementById('gv-archive-count')?.replaceChildren(document.createTextNode('…'));
  initArchiveVideosLoader();
}

function disabledPlaybackMessage(root) {
  const host = root || document.querySelector('.gv-page');
  return host?.dataset.disabledMessage?.trim() || 'پخش استریم در حال حاضر غیرفعال است.';
}

function showDisabledToast(root) {
  const message = disabledPlaybackMessage(root);
  if (typeof window.showSiteToast === 'function') {
    window.showSiteToast(message, 'warning');
    return;
  }
  alert(message);
}

function showErrorToast(message) {
  if (typeof window.showSiteToast === 'function') {
    window.showSiteToast(message, 'error');
    return;
  }
  alert(message);
}

function bindDisabledInteractions(root) {
  root.querySelectorAll('.gv-media-card, .gv-player-placeholder').forEach((el) => {
    el.addEventListener('click', (event) => {
      if (streamsEnabled(root)) return;
      event.preventDefault();
      event.stopPropagation();
      showDisabledToast(root);
    });
  });
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** 1250 → «1.3K» ؛ سرور استریم جدا برچسب آماده نمی‌فرستد. */
function compactCount(n) {
  const round1 = (x) => Math.round(x * 10) / 10;
  if (n >= 1e6) return round1(n / 1e6) + 'M';
  if (n >= 1e3) return round1(n / 1e3) + 'K';
  return String(n);
}

/** 3725 → «1:02:05» */
function formatDuration(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');

  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

function formatViewerLabel(stream) {
  const count = Number(stream.viewer_count) || 0;
  if (count <= 0) {
    return '';
  }

  return stream.viewer_label || compactCount(count);
}

/**
 * لیست استریم/ویدیو: اگر «سرور استریم جدا» فعال است (data-direct-*) مستقیم از آن سرور می‌خوانیم و
 * به کینگارو درخواستی نمی‌رود. فقط اگر آن سرور هنوز این endpoint را ندارد (۴۰۴، نسخهٔ قدیمی) به مسیر قدیمی برمی‌گردیم.
 */
async function fetchGameList(mount, dataKey, resolveUrl, endpoint, game) {
  let spec = null;
  try {
    spec = mount.dataset[dataKey] ? JSON.parse(mount.dataset[dataKey]) : null;
  } catch {
    spec = null;
  }

  if (spec) {
    const url = await resolveUrl(spec);
    if (url) {
      const resp = await fetch(url, { credentials: 'omit', headers: { Accept: 'application/json' } });
      if (resp.status !== 404) {
        return resp;
      }
    }
  }

  return fetch(`${endpoint}?game=${encodeURIComponent(game)}`, {
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
  });
}

/** «۲ ساعت و ۱۰ دقیقه» — مدت زمانی که استریم روی آنتن بوده. */
function formatUptime(startedAt) {
  if (!startedAt) {
    return '';
  }

  const started = Date.parse(startedAt);
  if (Number.isNaN(started)) {
    return '';
  }

  const minutes = Math.floor((Date.now() - started) / 60000);
  if (minutes < 1 || minutes > 60 * 24 * 7) {
    return '';
  }

  if (minutes < 60) {
    return `${minutes} دقیقه`;
  }

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  return rest > 0 ? `${hours} ساعت و ${rest} دقیقه` : `${hours} ساعت`;
}

function renderLiveStreamCard(stream) {
  const channel = escapeHtml(stream.channel_login || '');
  const channelName = escapeHtml(stream.channel_name || stream.channel_login || '');
  const title = escapeHtml(stream.title || '');
  const thumb = liveThumbStackHtml(stream.preview_url, stream.game_image_url);
  const viewers = formatViewerLabel(stream);
  const uptime = formatUptime(stream.started_at);

  const viewersHtml = viewers
    ? '<span class="gv-viewers">' +
      '<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><path d="M12 5c-5 0-8.6 4.2-9.7 6a1.5 1.5 0 0 0 0 1.6C3.4 14.4 7 18.6 12 18.6s8.6-4.2 9.7-6a1.5 1.5 0 0 0 0-1.6C20.6 9.2 17 5 12 5Zm0 10.6a3.8 3.8 0 1 1 0-7.6 3.8 3.8 0 0 1 0 7.6Z" fill="currentColor"/></svg>' +
      `<span>${escapeHtml(viewers)}</span>` +
      '</span>'
    : '';

  const uptimeHtml = uptime
    ? '<span class="gv-uptime">' +
      '<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.8" fill="none"/><path d="M12 7.5V12l3 1.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="none"/></svg>' +
      `<span>${escapeHtml(uptime)}</span>` +
      '</span>'
    : '';

  return (
    `<article class="gv-media-card gv-media-card--live gv-live-card" data-channel="${channel}" data-stream-title="${title || channelName}" role="button" tabindex="0" aria-label="پخش استریم ${channelName}">` +
    '<div class="gv-player-wrap">' +
    '<div class="gv-player-placeholder" aria-hidden="true">' +
    thumb +
    '<span class="gv-live-card__scrim"></span>' +
    '<span class="gv-live-card__play">' +
    '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><path d="M8 5.2v13.6L19 12 8 5.2Z" fill="currentColor"/></svg>' +
    '</span>' +
    '<span class="gv-live-tag"><i aria-hidden="true"></i>LIVE</span>' +
    viewersHtml +
    uptimeHtml +
    '</div>' +
    '<div class="gv-player-loading"><div class="gv-spinner"></div></div>' +
    '</div>' +
    '</article>'
  );
}

/** مجموع بینندگان را در هدر بخش می‌نویسد؛ اگر عددی نبود، چیپ را پنهان می‌کند. */
function updateLiveViewerSummary(streams) {
  const chip = document.getElementById('gv-live-viewers');
  if (!chip) {
    return;
  }

  const total = (streams || []).reduce((sum, stream) => sum + (Number(stream.viewer_count) || 0), 0);

  if (total <= 0) {
    chip.hidden = true;
    return;
  }

  chip.hidden = false;
  const value = chip.querySelector('.gv-stat-chip__value') || chip;
  value.textContent = total.toLocaleString('fa-IR');
}

/** کارت‌ها role="button" دارند؛ فعال‌سازی با Enter/Space را هم پشتیبانی می‌کنیم. */
function bindCardKeyboardActivation(root) {
  (root || document).querySelectorAll('.gv-media-card[role="button"]').forEach((card) => {
    if (card.dataset.gvKeyBound === '1') {
      return;
    }

    card.dataset.gvKeyBound = '1';
    card.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      card.click();
    });
  });
}

function liveSkeletonMarkup(count = 8) {
  const card = (
    '<div class="gv-skeleton-card" aria-hidden="true">' +
    '<div class="gv-skeleton-thumb"></div>' +
    '</div>'
  );

  return (
    '<div class="gv-skeleton-grid" role="status" aria-label="در حال بارگذاری استریم‌های زنده">' +
    card.repeat(count) +
    '</div>'
  );
}

function buildGamePath(mode, uuid) {
  const page = document.querySelector('.gv-page');
  const liveBase = (page?.dataset.gvLiveBase || '/stream').replace(/\/+$/, '');
  const archiveBase = (page?.dataset.gvArchiveBase || '/stream/archive').replace(/\/+$/, '');
  const base = mode === 'archive' ? archiveBase : liveBase;

  return `${base}/${encodeURIComponent(uuid)}/`;
}

function loadingMarkup(message) {
  return (
    '<div class="gv-live-loading">' +
    '<div class="gv-spinner"></div>' +
    `<p>${escapeHtml(message)}</p>` +
    '</div>'
  );
}

function destroyLivePlayer() {
  activeRecorderCleanup?.();
  activeRecorderCleanup = null;
  activeLiveController?.destroy?.();
  activeLiveController = null;
}

function destroyVodPlayer() {
  activeVodCleanup?.();
  activeVodCleanup = null;
}

function initGameFilterForms() {
  document.querySelectorAll('.gv-filter[data-gv-filter-mode]').forEach((form) => {
    if (form.dataset.gvFilterBound === '1') {
      return;
    }

    form.dataset.gvFilterBound = '1';

    form.addEventListener('submit', (event) => {
      const page = document.querySelector('.gv-page');
      const select = form.querySelector('#game');
      const uuid = select?.value?.trim();
      if (!uuid) {
        return;
      }

      const mode = form.dataset.gvFilterMode || 'live';
      const path = buildGamePath(mode, uuid);
      const currentPath = `${window.location.pathname.replace(/\/+$/, '')}/`;
      const targetPath = path.replace(/\/+$/, '') + '/';

      if (deferListToClient(page)) {
        event.preventDefault();

        if (currentPath === targetPath) {
          reloadStreamLists(mode);
          return;
        }

        window.location.assign(path);
        return;
      }

      if (!streamsEnabled(page)) {
        return;
      }

      event.preventDefault();

      if (currentPath === targetPath) {
        reloadStreamLists(mode);
        return;
      }

      window.location.assign(path);
    });
  });
}

function renderArchiveVideoCard(video) {
  const videoId = escapeHtml(video.video_id || '');
  const title = escapeHtml(video.title || '');
  const preview = video.preview_url
    ? `<img src="${escapeHtml(video.preview_url)}" alt="" loading="lazy" referrerpolicy="no-referrer">`
    : '<div class="gv-thumb-fallback">▶</div>';
  const durationHtml = Number(video.length_seconds) > 0
    ? `<span class="gv-duration">${escapeHtml(video.duration_label || formatDuration(video.length_seconds))}</span>`
    : '';
  const viewsHtml = Number(video.view_count) > 0
    ? `<span class="gv-viewers">👁 ${escapeHtml(video.views_label || compactCount(Number(video.view_count)))}</span>`
    : '';

  return (
    `<article class="product-item x-card gv-media-card" data-video-id="${videoId}" data-video-title="${title}" data-video-duration="${Number(video.length_seconds) || 0}">` +
    '<div class="gv-player-wrap">' +
    '<div class="gv-player-placeholder" role="button" tabindex="0" aria-label="پخش ویدیو">' +
    preview +
    durationHtml +
    viewsHtml +
    '</div>' +
    '<div class="gv-player-loading"><div class="gv-spinner"></div></div>' +
    '</div>' +
    '</article>'
  );
}

async function initArchiveVideosLoader() {
  const mount = document.getElementById('gv-archive-videos-mount');
  if (!mount) {
    return;
  }

  const generation = archiveLoadGeneration;
  const page = document.querySelector('.gv-page');
  const game = mount.dataset.game?.trim();
  const endpoint = mount.dataset.endpoint;
  const streamApi = mount.dataset.streamApi || '/stream/api/stream';
  const countBadge = document.getElementById('gv-archive-count');

  if (!game || !endpoint) {
    if (generation !== archiveLoadGeneration) {
      return;
    }

    mount.innerHTML = '<div class="gv-empty-state"><p>بازی انتخاب نشده است.</p></div>';
    if (countBadge) {
      countBadge.textContent = '۰ ویدیو';
    }
    return;
  }

  try {
    const resp = await fetchGameList(mount, 'directVideos', resolveDirectVideosUrl, endpoint, game);
    const json = await resp.json();

    if (generation !== archiveLoadGeneration) {
      return;
    }

    if (!resp.ok || !json.success) {
      throw new Error(json.error || 'خطا در دریافت ویدیوها');
    }

    const videos = Array.isArray(json.videos) ? json.videos : [];
    const resolvedStreamApi = json.stream_api || streamApi;

    if (countBadge) {
      countBadge.textContent = `${videos.length} ویدیو`;
    }

    if (!videos.length) {
      mount.innerHTML = (
        '<div class="gv-empty-state">' +
        '<div class="gv-empty-icon">📼</div>' +
        '<p>ویدیویی برای این بازی یافت نشد.</p>' +
        '</div>'
      );
      return;
    }

    mount.innerHTML =
      `<div class="gv-media-grid" data-gv-mode="vod" data-stream-api="${escapeHtml(resolvedStreamApi)}">` +
      videos.map(renderArchiveVideoCard).join('') +
      '</div>';

    bindDisabledInteractions(page || document);
    initVodTopPlayer();
    autoPlayVideoFromUrl();
  } catch (err) {
    if (generation !== archiveLoadGeneration) {
      return;
    }

    mount.innerHTML = (
      '<div class="gv-alert gv-alert-error">' +
      escapeHtml(err.message || 'خطا در دریافت ویدیوها') +
      '</div>'
    );
    if (countBadge) {
      countBadge.textContent = '۰ ویدیو';
    }
  }
}

async function initLiveStreamsLoader() {
  const mount = document.getElementById('gv-live-streams-mount');
  if (!mount) {
    return;
  }

  const generation = ++liveLoadGeneration;
  destroyLivePlayer();

  const page = document.querySelector('.gv-page');
  const game = mount.dataset.game?.trim();
  const endpoint = mount.dataset.endpoint;
  const playApi = mount.dataset.playApi || '/stream/api/live';
  const countBadge = document.getElementById('gv-live-count');

  if (!game || !endpoint) {
    if (generation !== liveLoadGeneration) {
      return;
    }

    mount.innerHTML = '<div class="gv-empty-state"><p>بازی انتخاب نشده است.</p></div>';
    if (countBadge) {
      countBadge.textContent = '۰ استریم';
    }
    updateLiveViewerSummary([]);
    return;
  }

  try {
    const resp = await fetchGameList(mount, 'directList', resolveDirectListUrl, endpoint, game);
    const json = await resp.json();

    if (generation !== liveLoadGeneration) {
      return;
    }

    if (!resp.ok || !json.success) {
      throw new Error(json.error || 'خطا در دریافت استریم‌ها');
    }

    const streams = Array.isArray(json.streams) ? json.streams : [];
    const resolvedPlayApi = json.play_api || playApi;

    if (countBadge) {
      countBadge.textContent = `${streams.length} استریم`;
    }

    updateLiveViewerSummary(streams);

    if (!streams.length) {
      mount.innerHTML = (
        '<div class="gv-empty-state gv-empty-state--live">' +
        '<div class="gv-empty-icon" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" width="30" height="30" fill="none"><path d="M5.6 5.6a9 9 0 0 0 0 12.8M18.4 5.6a9 9 0 0 1 0 12.8M8.5 8.5a5 5 0 0 0 0 7M15.5 8.5a5 5 0 0 1 0 7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><circle cx="12" cy="12" r="1.9" fill="currentColor"/></svg>' +
        '</div>' +
        '<p class="gv-empty-title">الان کسی زنده نیست</p>' +
        '<p class="gv-empty-desc">هیچ استریم آنلاینی برای این بازی پیدا نشد. کمی بعد دوباره سر بزنید یا سراغ آرشیو ویدیوها بروید.</p>' +
        '</div>'
      );
      return;
    }

    mount.innerHTML =
      `<div class="gv-media-grid gv-media-grid--live" data-gv-mode="live" data-play-api="${escapeHtml(resolvedPlayApi)}">` +
      streams.map(renderLiveStreamCard).join('') +
      '</div>';

    bindLiveThumbFallbacks(mount);
    bindDisabledInteractions(page || document);
    bindCardKeyboardActivation(mount);
    initLiveTopPlayer();
    autoPlayChannelFromUrl();
  } catch (err) {
    if (generation !== liveLoadGeneration) {
      return;
    }

    mount.innerHTML = (
      '<div class="gv-alert gv-alert-error">' +
      escapeHtml(err.message || 'خطا در دریافت استریم‌ها') +
      '</div>'
    );
    if (countBadge) {
      countBadge.textContent = '۰ استریم';
    }
    updateLiveViewerSummary([]);
  }
}

function initLiveTopPlayer() {
  destroyLivePlayer();

  const mount = document.getElementById('gv-top-player-mount');
  const grid = document.querySelector('.gv-media-grid[data-gv-mode="live"]');
  if (!mount || !grid) return;

  const page = document.querySelector('.gv-page');
  const playApi = grid.dataset.playApi || '/stream/api/live';

  const liveController = createLivePlayerController({
    slotId: 'gv-top-player-slot',
    wrapId: 'gv-top-player-wrap',
    cardSelector: '.gv-media-card[data-channel]',
    qualityStorageKey: 'gv-live-quality',
    useMobileModal: true,
    mobileLayoutMq: '(max-width: 1024px)',
    onClose: () => {
      grid.querySelectorAll('.gv-media-card').forEach((card) => {
        card.classList.remove('is-active');
        card.querySelector('.gv-player-loading')?.classList.remove('active');
      });
    },
  });

  liveController.setupDock();

  liveController.bindCards({
    container: grid,
    getKey: (card) => card.dataset.channel,
    onBeforePlay: () => {
      if (!streamsEnabled(page)) {
        showDisabledToast(page);
        return false;
      }
      return true;
    },
    fetchStreamUrl: async (channel) => {
      const card = grid.querySelector(`.gv-media-card[data-channel="${CSS.escape(channel)}"]`);
      card?.querySelector('.gv-player-loading')?.classList.add('active');

      try {
        const direct = await resolveDirectStreamUrl('live', channel);
        if (direct) {
          return direct;
        }

        const resp = await fetch(`${playApi}?channel=${encodeURIComponent(channel)}`, {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        const json = await resp.json();

        if (json.disabled) {
          showDisabledToast(page);
          throw new Error('disabled');
        }

        if (!resp.ok || !json.success || !json.data?.stream_url) {
          throw new Error(json.error || 'خطا در پخش زنده');
        }

        return json.data.stream_url;
      } finally {
        card?.querySelector('.gv-player-loading')?.classList.remove('active');
      }
    },
    onPlayError: (err) => {
      if (err?.message !== 'disabled') {
        showErrorToast('خطا در پخش استریم');
      }
    },
    onPlayStarted: (channel, card) => {
      trackStreamPlay({
        fromEl: card || grid,
      });
    },
  });

  activeLiveController = liveController;

  activeRecorderCleanup?.();
  activeRecorderCleanup = initStreamLiveRecorder({
    liveController,
    wrapId: 'gv-top-player-wrap',
    page,
  });
}

function autoPlayChannelFromUrl() {
  const page = document.querySelector('.gv-page');
  const channel = page?.dataset.autoChannel?.trim().toLowerCase();
  if (!channel) {
    return;
  }

  const card = document.querySelector(`.gv-media-card[data-channel="${CSS.escape(channel)}"]`);
  if (!card) {
    return;
  }

  requestAnimationFrame(() => {
    card.click();
  });
}

function autoPlayVideoFromUrl() {
  const page = document.querySelector('.gv-page');
  const videoId = page?.dataset.autoVideoId?.trim();
  if (!videoId) {
    return;
  }

  const card = document.querySelector(`.gv-media-card[data-video-id="${CSS.escape(videoId)}"]`);
  if (!card) {
    return;
  }

  requestAnimationFrame(() => {
    card.click();
  });
}

function initVodTopPlayer() {
  destroyVodPlayer();
  activeVodSaverCleanup?.();
  activeVodSaverCleanup = null;
  activeVodGalleryCleanup?.();
  activeVodGalleryCleanup = null;

  const mount = document.getElementById('gv-top-player-mount');
  const grid = document.querySelector('.gv-media-grid[data-gv-mode="vod"]');
  const streamUrl = grid?.dataset.streamApi
    || document.querySelector('.gv-page')?.dataset.gvStreamUrl;
  if (!mount || !grid || !streamUrl) return;

  const page = document.querySelector('.gv-page');
  const playerWrap = document.getElementById('gv-top-player-wrap');
  const video = playerWrap?.querySelector('video');
  if (!playerWrap || !video) return;

  let activeHls = null;
  let activeKey = null;
  let vodGeneration = 0;
  const urlCache = new Map();
  const MAX_VOD_CACHE = 20;

  const cacheVodUrl = (videoId, value) => {
    if (urlCache.has(videoId)) {
      urlCache.delete(videoId);
    }

    urlCache.set(videoId, value);

    while (urlCache.size > MAX_VOD_CACHE) {
      const oldestKey = urlCache.keys().next().value;
      urlCache.delete(oldestKey);
    }
  };

  // Paused VOD: stop fetching through the relay after a while, resume on play.
  let vodIdleTimer = null;
  let vodLoadSuspended = false;
  const onVodPause = () => {
    clearTimeout(vodIdleTimer);
    vodIdleTimer = setTimeout(() => {
      vodIdleTimer = null;
      if (video.paused && activeHls) {
        try {
          activeHls.stopLoad();
          vodLoadSuspended = true;
        } catch {
          // ignore
        }
      }
    }, PAUSE_IDLE_STOP_MS);
  };
  const onVodPlay = () => {
    clearTimeout(vodIdleTimer);
    vodIdleTimer = null;
    if (vodLoadSuspended && activeHls) {
      vodLoadSuspended = false;
      try {
        activeHls.startLoad(-1);
      } catch {
        // ignore
      }
    }
  };
  video.addEventListener('pause', onVodPause);
  video.addEventListener('play', onVodPlay);

  const stopVodPlayback = () => {
    clearTimeout(vodIdleTimer);
    vodIdleTimer = null;
    vodLoadSuspended = false;
    if (activeHls) {
      activeHls.destroy();
      activeHls = null;
    }

    video.pause();
    video.removeAttribute('src');
    video.load();
    activeKey = null;
  };

  const playerDock = setupPlayerDock({
    slotId: 'gv-top-player-slot',
    wrapId: 'gv-top-player-wrap',
    cardSelector: '.gv-media-card[data-video-id]',
    onClose: stopVodPlayback,
  });

  grid.querySelectorAll('.gv-media-card[data-video-id]').forEach((card) => {
    card.addEventListener('click', async () => {
      if (!streamsEnabled(page)) {
        showDisabledToast(page);
        return;
      }

      const videoId = card.dataset.videoId;
      if (!videoId) return;

      const generation = ++vodGeneration;

      if (activeKey !== videoId) {
        stopVodPlayback();
      }

      stopOtherLivePlayers();

      document.querySelectorAll('video').forEach((el) => {
        if (el !== video) {
          el.pause();
        }
      });

      grid.querySelectorAll('.gv-media-card').forEach((item) => {
        item.classList.toggle('is-active', item === card);
      });

      const loading = card.querySelector('.gv-player-loading');
      loading?.classList.add('active');

      playerWrap.classList.add('is-open');
      playerDock?.startObserver();

      try {
        let cached = urlCache.get(videoId);
        // سرور استریم جدا: مستقیم از آن می‌خوانیم (بدون درخواست به کینگارو)؛ کش نمی‌شود چون توکن منقضی می‌شود.
        const directVod = await resolveDirectStreamUrl('vod', videoId);
        if (directVod) {
          cached = { url: directVod, type: 'hls' };
        }

        if (!cached) {
          const resp = await fetch(`${streamUrl}?video_id=${encodeURIComponent(videoId)}`, {
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          });
          const json = await resp.json();

          if (generation !== vodGeneration) {
            return;
          }

          if (json.disabled) {
            showDisabledToast(page);
            throw new Error('disabled');
          }

          if (!resp.ok || !json.success) {
            throw new Error(json.error || 'خطا در بارگذاری ویدیو');
          }

          cached = {
            url: json.data.stream_url,
            type: json.data.stream_type || 'hls',
          };
          cacheVodUrl(videoId, cached);
        }

        if (generation !== vodGeneration) {
          return;
        }

        await loadHlsScript();

        if (generation !== vodGeneration) {
          return;
        }

        activeHls = window.GameVodPlayer.attach(video, cached.url, cached.type);
        activeKey = videoId;
        await video.play();
        trackStreamPlay({
          fromEl: card || mount,
        });
      } catch (err) {
        if (generation !== vodGeneration) {
          return;
        }

        playerWrap.classList.remove('is-open', 'is-docked');
        playerDock?.stopObserver();
        card.classList.remove('is-active');
        stopVodPlayback();
        if (err?.message !== 'disabled') {
          showErrorToast(err.message || 'خطا در بارگذاری ویدیو');
        }
      } finally {
        if (generation === vodGeneration) {
          loading?.classList.remove('active');
        }
      }
    });
  });

  activeVodCleanup = () => {
    vodGeneration += 1;
    stopVodPlayback();
    video.removeEventListener('pause', onVodPause);
    video.removeEventListener('play', onVodPlay);
    playerDock?.cleanup();
    activeVodSaverCleanup?.();
    activeVodSaverCleanup = null;
    activeVodGalleryCleanup?.();
    activeVodGalleryCleanup = null;
  };

  activeVodSaverCleanup = initStreamVodSaver({
    page,
    getActiveVideoId: () => activeKey,
    streamApi: streamUrl,
  });

  activeVodGalleryCleanup = initStreamVodGallery({
    page,
    getActiveVideoId: () => activeKey,
  });
}

let streamCatalogAbortController = null;
let streamCatalogRequestId = 0;

function buildStreamCatalogBrowserUrl(section, { search = '', page = 1 } = {}) {
  const browserUrl = new URL(section.dataset.searchBase || window.location.pathname, window.location.origin);
  const keyword = String(search ?? '').trim();

  if (keyword) {
    browserUrl.searchParams.set('search', keyword);
  } else {
    browserUrl.searchParams.delete('search');
  }

  if (page > 1) {
    browserUrl.searchParams.set('page', String(page));
  } else {
    browserUrl.searchParams.delete('page');
  }

  return browserUrl;
}

async function loadStreamCatalog({ search, page = 1, pushUrl = true } = {}) {
  const section = document.getElementById('gv-stream-catalog-section');
  const results = document.getElementById('gv-stream-catalog-results');
  const input = document.getElementById('gv-stream-search');

  if (!section || !results) {
    return;
  }

  const endpoint = section.dataset.catalogEndpoint;
  if (!endpoint) {
    return;
  }

  const keyword = search ?? input?.value?.trim() ?? '';
  const requestUrl = new URL(endpoint, window.location.origin);

  if (keyword) {
    requestUrl.searchParams.set('search', keyword);
  }

  requestUrl.searchParams.set('page', String(Math.max(1, page)));

  const catalogMode = section.dataset.catalogMode || 'live';
  if (catalogMode === 'archive') {
    requestUrl.searchParams.set('mode', 'archive');
  }

  streamCatalogAbortController?.abort();
  streamCatalogAbortController = new AbortController();
  const requestId = ++streamCatalogRequestId;

  results.classList.add('is-loading');
  results.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(requestUrl, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      signal: streamCatalogAbortController.signal,
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'خطا در جستجو');
    }

    if (requestId !== streamCatalogRequestId) {
      return;
    }

    results.innerHTML = data.html;
    bindLiveThumbFallbacks(results);

    if (pushUrl) {
      const browserUrl = buildStreamCatalogBrowserUrl(section, {
        search: keyword,
        page: data.page || page,
      });
      const next = `${browserUrl.pathname}${browserUrl.search}`;
      const current = `${window.location.pathname}${window.location.search}`;

      if (next !== current) {
        history.replaceState({ gvCatalog: true }, '', next);
      }
    }
  } catch (error) {
    if (error?.name === 'AbortError') {
      return;
    }

    showErrorToast(error?.message || 'خطا در جستجو');
  } finally {
    if (requestId === streamCatalogRequestId) {
      results.classList.remove('is-loading');
      results.removeAttribute('aria-busy');
    }
  }
}

function initStreamCatalogSearch() {
  const section = document.getElementById('gv-stream-catalog-section');
  const results = document.getElementById('gv-stream-catalog-results');
  const input = document.getElementById('gv-stream-search');

  if (!section || !results || !input || section.dataset.gvCatalogBound === '1') {
    return;
  }

  section.dataset.gvCatalogBound = '1';
  bindLiveThumbFallbacks(results);

  let timer = null;

  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      loadStreamCatalog({ search: input.value, page: 1 });
    }, 400);
  });

  results.addEventListener('click', (event) => {
    const link = event.target.closest('.origin-pagination a.page-link');
    if (!link) {
      return;
    }

    event.preventDefault();

    const url = new URL(link.href, window.location.origin);
    loadStreamCatalog({
      search: url.searchParams.get('search') || '',
      page: parseInt(url.searchParams.get('page') || '1', 10),
    });
  });

  if (!window.__gvCatalogPopstateBound) {
    window.__gvCatalogPopstateBound = true;

    window.addEventListener('popstate', () => {
      const activeSection = document.getElementById('gv-stream-catalog-section');
      const activeInput = document.getElementById('gv-stream-search');

      if (!activeSection || !activeInput) {
        return;
      }

      const url = new URL(window.location.href);
      activeInput.value = url.searchParams.get('search') || '';

      loadStreamCatalog({
        search: activeInput.value,
        page: parseInt(url.searchParams.get('page') || '1', 10),
        pushUrl: false,
      });
    });
  }
}

function initAdminTestPlayer() {
  const video = document.getElementById('gv-admin-test-player');
  if (!video || !window.GameVodPlayer) return;

  const url = video.dataset.streamUrl;
  const type = video.dataset.streamType || 'hls';
  if (!url) return;

  window.GameVodPlayer.attach(video, url, type);
  video.play().catch(() => {});
}

function bootGameVodsPage() {
  const page = document.querySelector('.gv-page');
  if (!page) {
    return;
  }

  initXSelects(document);
  bindHeroPosterFallback(page);
  bindDisabledInteractions(page);
  initGameFilterForms();
  initStreamCatalogSearch();
  initArchiveVideosLoader();

  initLiveStreamsLoader();

  initAdminTestPlayer();
}

// Leaving the page via wire:navigate must stop live/VOD playback, otherwise the
// detached <video> + hls.js instance keep pulling segments through the relay.
document.addEventListener('livewire:navigating', () => {
  liveLoadGeneration += 1;
  archiveLoadGeneration += 1;
  destroyLivePlayer();
  destroyVodPlayer();
});

document.addEventListener('DOMContentLoaded', bootGameVodsPage);
document.addEventListener('livewire:navigated', bootGameVodsPage);
